import DateHelper from "date-helper-util";
import {Route} from "vue-router";

export type FrontendFilterResultRowPreprocessor = (resultRow: Record<string,any>) => Record<string,any>;

export interface FrontendFilterGroup {

    // De groepnaam voor deze filters. Bijvoorbeeld order-search, location-index, etc. Wordt (o.a.?) gebruikt voor translation keys van de filters
    identifier: string;
    // Soms worden groepen meerdere keer ingezet waarbij dmv overerving subtiele dingen worden aangepast. Zoals de getResultLink().
    // Die subdefinities moeten wel een eigen unieke identifier hebben, terwijl wel de vertaling van de hoofdgroep gewoon gebruikt worden via de hoofd-identifier.
    subIdentifier: string;

    pageTitleLanguageKey: string;
    pageIcon: string;

    hideFilterCard?: boolean;

    searchApiPath: string | ((route: Route) => string);

    searchApiDefaultParameters?: Record<string, any> | ((filterValues: FrontendFilterValues) => Record<string, any>);

    resultsPerPage: number;

    // Een callback waar elk zoekresultaat doorheen wordt gedaan waarin alvast wat voorverwerking kan worden gedaan. Zoals sortering van subrelaties.
    resultRowPreprocessor?: FrontendFilterResultRowPreprocessor;
    resultRowModifiers?: (() => FrontendFilterResultRowModifier)[];

    /**
     * @param result
     */
    getResultLink?: (result: Record<string,string>, filterValues: FrontendFilterValues) => Record<string,any>;

    batchActions: FrontendFilterBatchAction[];

    // Geef een dynamisch ingeladen Vue component terug (via Vue.component()) om te tonen boven de filters
    headerComponent?: () => object;

    // De lijst van beschikbare filters
    filters: FrontendFilter[];

    columns: FrontendFilterResultColumn[];
}

export class FrontendFilterOption {
    // De waarde zoals die wordt gestuurd naar de API
    value: string;
    // De tekst die wordt getoond in de GUI
    text: string;

    // Extra informatie over de optie om op te filteren in de filteredOptions()
    extra: Record<string, unknown>

    constructor(value: string, text: string, extra?: Record<string, unknown>) {
        this.value = value;
        this.text = text;
        this.extra = extra ?? {}
    }
}

export class FrontendFilter {

    // Systeemnaam voor de filter. Wordt gebruikt om naar te verwijzen in search filter sets etc.
    identifier: string;

    // Geeft aan welk type invoerveld gebruikt moet worden.
    inputType: FrontendFilterInputType;

    // De mogelijke invoeropties (indien van toepassing). Moeten via een Promise worden aangeleverd ivm mogelijk ophalen van de data
    getOptions?: (() => Promise<FrontendFilterOption[]>) | FrontendFilterOption[];

    // Soms kunnen filteropties worden beperkt afhankelijk van bijvoorbeeld een andere filter. Denk aan de locatietype + locatie filter-combinatie.
    // Hier kan een functie worden gedefinieerd die de gefilterde opties returned. Achterwege laten betekent altijd alle opties beschikbaar laten.
    filteredOptions: (options: FrontendFilterOption[], filterValues: FrontendFilterValues) => FrontendFilterOption[] | Promise<FrontendFilterOption[]>;

    // De waarde van het filter wordt door deze functie gehaald voordat deze meegegeven wordt aan de search-API. Bijvoorbeeld om datum-formats te standaardiseren.
    normalizeValue?: (filterValues: FrontendFilterValues) => FrontendFilterValues;

    // Verberg de filter obv bepaalde voorwaarden. Bijvoorbeeld omdat deze pas relevant wordt icm specifieke waardes bij andere filters
    show: () => boolean;

    constructor(
        identifier: string,
        inputType: FrontendFilterInputType,
        getOptions?: (() => Promise<FrontendFilterOption[]>) | FrontendFilterOption[],
        filteredOptions?: (options: FrontendFilterOption[], filterValues: FrontendFilterValues) => FrontendFilterOption[] | Promise<FrontendFilterOption[]>,
        normalizeValue?: (filterValues: FrontendFilterValues) => FrontendFilterValues,
        show?: () => boolean,

    ) {
        this.identifier = identifier;
        this.inputType = inputType;
        this.getOptions = getOptions;
        this.filteredOptions = filteredOptions ?? (() => this.options)
        this.normalizeValue = normalizeValue;
        this.show = show ?? (() => true);
    }

    // Internal vars
    options: FrontendFilterOption[] = [];
}

export class FrontendFilterResultRowModifier {
    test: (resultRow: Record<string,any>, filterValues: FrontendFilterValues) => boolean;
    cssClass?: FrontendFilterResultRowModifierCssClasses;
    icon?: string;
    iconCssClass?: string;
    iconTooltipLanguageKey?: string;
    isSelectableForBatchActions: boolean;

    constructor(
        test: (resultRow: Record<string,any>, filterValues: FrontendFilterValues) => boolean,
        cssClass?: FrontendFilterResultRowModifierCssClasses,
        icon?: string,
        iconCssClass?: string,
        iconTooltipLanguageKey?: string,
        isSelectableForBatchActions: boolean = true,
    ) {
        this.test = test;
        this.cssClass = cssClass;
        this.icon = icon;
        this.iconCssClass = iconCssClass
        this.iconTooltipLanguageKey = iconTooltipLanguageKey
        this.isSelectableForBatchActions = isSelectableForBatchActions;
    }
}

export enum FrontendFilterResultRowModifierCssClasses {
    strikethrough = 'strikethrough',
    highlight = 'highlight',
    striped = 'striped',
}

export class FrontendFilterResultColumn {

    // Systeemnaam voor de kolom. Wordt gebruikt om naar te verwijzen in search filter sets etc.
    identifier: string;

    // Haal de waarde op van de kolom. Dit gebeurt via een functie, omdat mogelijk de waarde nog omgevormd moet worden
    getValue: (resultRow: Record<string,any>) => string | object | undefined;

    // Preload data om te tonen in de kolom. Als de kolom zichtbaar is wordt deze Promise uitgevoerd
    preload?: () => Promise<void>;

    // Verwijs naar een identifier van een filter om filteren vanuit de kolomtitel mogelijk te maken
    resultColumnFilterIdentifier?: string;

    constructor(
        identifier: string,
        getValue: (resultRow: Record<string,any>) => string | object | undefined,
        preload?: () => Promise<void>,
        resultColumnFilterIdentifier?: string,
    ) {
        this.identifier = identifier;
        this.getValue = getValue;
        this.preload = preload;
        this.resultColumnFilterIdentifier = resultColumnFilterIdentifier;
    }
}

export type FrontendFilterValues = {
    [filterIdentifier: string]: any;
}

export class FrontendFilterConfig {
    // De lijst van filters die beschikbaar is voor deze pagina. Wordt gedefinieerd in een SearchFilterSet.
    // Filters die beschikbaar zijn, zijn vanzelfsprekend ook zichtbaar. Dus daar is geen onderscheid zoals bij kolommen.
    availableFilterIdentifiers: string[];

    // De lijst van kolommen die beschikbaar is voor deze pagina. Wordt gedefinieerd in een SearchFilterSet.
    availableColumnIdentifiers: string[];

    // Niet alle kolommen die beschikbaar zijn wil je ook tonen
    visibleColumnIdentifiers: string[];

    // De waardes voor de filters: {filternaam: "waarde"}
    filterValues: FrontendFilterValues;

    filtersExpanded: boolean;

    constructor(
        availableFilterIdentifiers: string[],
        availableColumnIdentifiers: string[],
        visibleColumnIdentifiers: string[],
        filterValues: FrontendFilterValues,
        filtersExpanded: boolean,
    ) {
        this.availableFilterIdentifiers = availableFilterIdentifiers;
        this.availableColumnIdentifiers = availableColumnIdentifiers;
        this.visibleColumnIdentifiers = visibleColumnIdentifiers;
        this.filterValues = filterValues;
        this.filtersExpanded = filtersExpanded;
    }
}

export type FrontendFilterPreset = {
    created_at: string | null;
    updated_at: string | null;
    group: string;
    identifier: string;
    filters_expanded: boolean;
    description: string | null;
    available_filters: string[] | null;
    available_columns: string[] | null;
    visible_columns: string[] | null;
    filter_values: Record<string,unknown>;
    locked_filters: string[] | null;
}

/** Input types */
type FrontendFilterInputTypeDefaultConfigs = {
    disabled?: (filter: FrontendFilter, filterValues: FrontendFilterValues) => boolean,
    readonly?: (filter: FrontendFilter, filterValues: FrontendFilterValues) => boolean,
    min?: Function,
    max?: Function,
    placeholder?: Function,
    clearValueLink?: Boolean,
}

export class FrontendFilterBatchAction {
    identifier: string;

    show: () => boolean;

    // Geef een functie op die de geselecteerde resultaten als argument ontvangt en deze verwerkt in een Promise.
    // OF: Geef een functie op die dynamisch een component inlaadt om te tonen in een modal. Dus bijvoorbeeld: () => Vue.component('BatchAddOpeningTimes', () => import('../../views/Locations/Modals/BatchAddOpeningTimes.vue'))
    execute: ((selectedResults: Record<string,any>[]) => Promise<boolean> | object);

    constructor(
        identifier: string,
        show: () => boolean,
        execute: ((selectedResults: Record<string,any>[]) => Promise<boolean> | object),
    ) {
        this.identifier = identifier;
        this.show = show;
        this.execute = execute;
    }
}

// Abstract input types

abstract class FrontendFilterInputType {
    config: Record<string, unknown> & FrontendFilterInputTypeDefaultConfigs = {};
    valueType: unknown;

    protected constructor(config?: Record<string,any>) {
        this.config = config ?? {};
    }

    public getDisplayValue(value: any, options: FrontendFilterOption[] | undefined): string {
        return value.toString()
    }
}

abstract class FrontendFilterInputWithOptions extends FrontendFilterInputType {
    public getDisplayValue(value: any, options: FrontendFilterOption[] | undefined): string {
        if (!Array.isArray(value)) {
            value = [value]
        }

        const displayValues: string[] = []
        value.forEach((singleValue: any) => {
            displayValues.push(
                options?.find((option: FrontendFilterOption) => option.value === singleValue)?.text as string
            )
        })

        return displayValues.join(', ')
    }
}

// Real input types

export class FrontendFilterInputText extends FrontendFilterInputType {
    constructor({}: FrontendFilterInputTypeDefaultConfigs & {
        placeholder?: (filterValues: FrontendFilterValues) => string;
    } = {}) {
        super(arguments[0]);
    }

    valueType = String;
}

export class FrontendFilterInputSelectOne extends FrontendFilterInputWithOptions {
    constructor({}: FrontendFilterInputTypeDefaultConfigs = {}) {
        super(arguments[0]);
    }

    valueType = String;
}

export class FrontendFilterInputSelectOneWithSearch extends FrontendFilterInputSelectOne {

}

export class FrontendFilterInputSelectMultipleWithSearch extends FrontendFilterInputType {
    constructor({}: FrontendFilterInputTypeDefaultConfigs = {}) {
        super(arguments[0]);
    }

    valueType = Array;
}

export class FrontendFilterInputBooleanSelect extends FrontendFilterInputWithOptions {
    constructor({}: FrontendFilterInputTypeDefaultConfigs = {}) {
        super(arguments[0]);
    }

    valueType = Boolean;
}

export class FrontendFilterInputNumber extends FrontendFilterInputType {
    constructor({}: FrontendFilterInputTypeDefaultConfigs & {
        min?: (filterValues: FrontendFilterValues) => number | undefined;
        max?: (filterValues: FrontendFilterValues) => number | undefined;
        placeholder?: (filterValues: FrontendFilterValues) => string;
    } = {}) {
        super(arguments[0]);
    }

    valueType = String;
}

export class FrontendFilterInputDate extends FrontendFilterInputType {
    constructor({}: FrontendFilterInputTypeDefaultConfigs & {
        min?: (filterValues: FrontendFilterValues) => string | undefined;
        max?: (filterValues: FrontendFilterValues) => string | undefined;
    } = {}) {
        super(arguments[0]);
    }

    valueType = String;

    public getDisplayValue(value: any, options: FrontendFilterOption[] | undefined): string {
        try {
            return DateHelper(value, 'YYYY-MM-DD').getFormated('DD-MM-YYYY').toString()
        } catch (e) {
            return ''
        }
    }
}

export class FrontendFilterInputMultiple extends FrontendFilterInputType {
    constructor({}: FrontendFilterInputTypeDefaultConfigs & {} = {}) {
        super(arguments[0]);
    }

    valueType = Array;

    public getDisplayValue(value: any, options: FrontendFilterOption[] | undefined): string {
        return value.join(', ')
    }
}


